<template>
  <Dialog v-model:open="open">
    <DialogTrigger>
      <Button variant="outline" size="sm">Edit Invoice details</Button>
    </DialogTrigger>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Edit Invoice Details</DialogTitle>
      </DialogHeader>
      <form
        id="update-invoice-details"
        class="p-7 pb-6 pt-5 space-y-4"
        @submit="onSubmit"
      >
        <fieldset class="flex flex-col gap-2">
          <Label for="companyName"> Company Name </Label>
          <Input
            v-bind="companyNameAttrs"
            id="companyName"
            v-model="companyName"
          />
          <span class="text-error-400">{{ errors?.companyName }}</span>
        </fieldset>
        <fieldset class="flex flex-col gap-2">
          <Label for="country"> Country </Label>
          <select
            v-bind="countryAttrs"
            id="country"
            v-model="country"
            class="h-10 w-full rounded border border-primary-400 autofill:bg-primary-400 bg-primary-700 text-neutral-300 placeholder:text-neutral-500 ring-offset-neutral-900 focus:ring-2 focus:ring-offset-[0px] focus:ring-neutral-300/20 px-3 py-2 text-sm"
          >
            <option value="">Select a country</option>
            <option v-for="(item, key) in countries" :key="key" :value="key">
              {{ item }}
            </option>
          </select>
          <span class="text-error-400">{{ errors?.country }}</span>
        </fieldset>
        <fieldset class="flex flex-col gap-2">
          <Label for="address"> Address </Label>
          <Input v-bind="addressAttrs" id="address" v-model="address" />
          <span class="text-error-400">{{ errors?.address }}</span>
        </fieldset>
        <div class="flex gap-8">
          <div class="w-1/2">
            <fieldset class="flex flex-col gap-2">
              <Label for="city"> City </Label>
              <Input v-bind="cityAttrs" id="city" v-model="city" />
              <span class="text-error-400">{{ errors?.city }}</span>
            </fieldset>
          </div>
          <div class="w-1/2">
            <fieldset class="flex flex-col gap-2">
              <Label for="state"> State </Label>
              <Input v-bind="stateAttrs" id="state" v-model="state" />
              <span class="text-error-400">{{ errors?.state }}</span>
            </fieldset>
          </div>
        </div>
        <div class="flex gap-8">
          <div class="w-1/2">
            <fieldset class="flex flex-col gap-2">
              <Label for="zipCode"> Zip / Postal Code </Label>
              <Input v-bind="zipCodeAttrs" id="zipCode" v-model="zipCode" />
              <span class="text-error-400">{{ errors?.zipCode }}</span>
            </fieldset>
          </div>
          <div class="w-1/2">
            <fieldset class="flex flex-col gap-2">
              <Label for="vatId"> VAT ID </Label>
              <Input v-bind="vatIdAttrs" id="vatId" v-model="vatId" />
              <span class="text-error-400">{{ errors?.vatId }}</span>
            </fieldset>
          </div>
        </div>
      </form>
      <DialogFooter>
        <DialogClose as-child>
          <Button type="button" variant="ghost" size="sm" :disabled="isPending">
            Cancel
          </Button>
        </DialogClose>
        <Button
          type="submit"
          :loading="isPending"
          :disabled="isPending"
          form="update-invoice-details"
          size="sm"
        >
          Submit
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  DialogHeader,
  DialogClose,
  DialogFooter,
} from '@ui/components/dialog';
import { Button } from '@ui/components/button';
import { Label } from '@ui/components/label';
import { Input } from '@ui/components/input';
import requests from '@/vuex/requests.js';
import { toast } from 'vue-sonner';

const store = useStore();

const countries = computed(() => store.getters['settings/getCountries']);
const open = ref(false);
const queryClient = useQueryClient();
const props = defineProps({
  details: {
    type: Object,
    required: true,
  },
});

const validationSchema = toTypedSchema(
  z.object({
    companyName: z.string().max(255),
    address: z.string().max(255),
    city: z.string().max(255),
    state: z.string().max(255),
    zipCode: z.string().max(255),
    vatId: z.string().max(255).nullable(),
    country: z.string().refine(country => country.length > 0, {
      message: 'Country is required',
    }),
  })
);

const { handleSubmit, errors, defineField, setFieldError, resetForm } = useForm(
  {
    validationSchema,
    initialValues: props.details,
  }
);

watch(open, () => {
  resetForm({
    values: props.details,
  });
});

const [companyName, companyNameAttrs] = defineField('companyName');
const [address, addressAttrs] = defineField('address');
const [city, cityAttrs] = defineField('city');
const [state, stateAttrs] = defineField('state');
const [zipCode, zipCodeAttrs] = defineField('zipCode');
const [vatId, vatIdAttrs] = defineField('vatId');
const [country, countryAttrs] = defineField('country');

const { isPending, mutate } = useMutation({
  mutationFn: async payload => {
    const res = await requests.workspaces.subscription.updateInvoiceDetails(
      store.getters['workspace/getId'],
      {
        ...payload,
      }
    );
    return res;
  },
  onSuccess: async () => {
    toast.success('Your Billing Details were updated');
    queryClient.invalidateQueries({
      queryKey: ['workspaceSubscriptionBillingInfo'],
    });
    open.value = false;
  },
  onError: async error => {
    console.error(error);
    toast.error(
      error?.response?.data?.title ||
        'Something went wrong, please try again later'
    );
  },
});

const onSubmit = handleSubmit(async values => {
  if (values.vatId) {
    const validateVatRes = await requests.workspaces.subscription.validateVat(
      store.getters['workspace/getId'],
      {
        vat: values.vatId,
      }
    );

    if (validateVatRes.response?.status === 400) {
      setFieldError(
        'vatId',
        validateVatRes?.data?.detail || 'Your VAT number is not correct'
      );

      return;
    }
  }

  mutate(values);
});
</script>
